import { ReactElement, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useAtomValue } from 'jotai'

import SyfDisputes, { DisputeFeature, TransactionProps } from 'digital-disputes'
import { selectedAccountAtom } from 'store/atoms'
import { useAccountSummary } from 'const/syfApiQueries'
import getMFEConfig from 'helpers/getMFEConfig'
import { getBrandRefreshPhase } from 'helpers/getBrandRefreshPhase'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import useProfileNavigate from 'hooks/useProfileNavigate'
import { setAnalyticsDefaults } from 'helpers/triggerAnalyticsEvent'

export const ELIGIBILITY_BUTTON_PORTAL_ID = 'eligibility-button-portal'
export const ELIGIBILITY_STATUS_PORTAL_ID = 'eligibility-status-portal'

export interface DisputesEligibilityPortalProps {
  transaction: TransactionProps | null
  navigationHandler: (feature: string) => void
  returnToParent: (flow: DisputeFeature, transaction: TransactionProps) => void
}

const DisputesEligibilityPortal = ({
  transaction,
  navigationHandler,
  returnToParent: onReturnToParent
}: DisputesEligibilityPortalProps): ReactElement | null => {
  const selectedAccount = useAtomValue(selectedAccountAtom)
  const { data: { product } = {} } = useAccountSummary(selectedAccount)

  const disputesConfig = getMFEConfig('disputes')
  const brandRefreshPhase = getBrandRefreshPhase()
  const fraudDisputesEnabled = isFeatureFlagEnabled('isFraudDisputesEnabled')
  const { handleProfileClick } = useProfileNavigate()

  useEffect(() => {
    if (!transaction) return
    if (!product?.clientId) return
    setAnalyticsDefaults({ ClientName: product.clientId })
  }, [transaction, product?.clientId])

  const NOOP = () => {}

  const target = document.getElementById(ELIGIBILITY_BUTTON_PORTAL_ID)

  if (!transaction || !target) return null

  return createPortal(
    <SyfDisputes
      disputesMFEConfig={disputesConfig}
      accountId={selectedAccount}
      eligibilityStatusPortalId={ELIGIBILITY_STATUS_PORTAL_ID}
      eligibilityButtonPortalId={ELIGIBILITY_BUTTON_PORTAL_ID}
      isFraudDisputeExperienceEnabled
      fraudDisputesEnabled={fraudDisputesEnabled}
      feature={DisputeFeature.Eligibility}
      navigationHandler={navigationHandler}
      returnToParent={(flow) => onReturnToParent(flow, transaction)}
      transaction={transaction}
      handleParentCallback={NOOP}
      handleProfileClick={handleProfileClick}
      brandRefreshPhase={brandRefreshPhase}
    />,
    target
  )
}

export default DisputesEligibilityPortal
